import { atom, useAtomValue, useSetAtom } from "jotai";
import { atomFamily } from "jotai/utils";
import { useCallback, useMemo } from "react";
import { v4 as uuid } from "uuid";

import { useCurrentAccount } from "@/providers/account";

import { targetToPath } from "./routeCodec";
import { Tab } from "./tabTarget";
import * as ops from "./tabsReducer";
import { TabsState } from "./tabsReducer";
import { loadTabsState, saveTabsState } from "./tabsStorage";

/**
 * React bindings for the tab strip. Every state transition lives in `tabsReducer`; this module only
 * wires them to Jotai, to localStorage, and to the account scope.
 */

/**
 * Writes through to localStorage on every transition, so persistence is a property of the store
 * rather than of whichever component happens to be mounted.
 */
const tabsStateAtom = atomFamily((accountId: string) => {
  const base = atom<TabsState>(loadTabsState(accountId));
  return atom(
    (get) => get(base),
    (get, set, update: (prev: TabsState) => TabsState) => {
      const prev = get(base);
      const next = update(prev);
      if (next === prev) return;
      set(base, next);
      saveTabsState(accountId, next);
    },
  );
});

export function useTabsState() {
  const { id } = useCurrentAccount();
  return useAtomValue(tabsStateAtom(id));
}

/** Applies a reducer op. Stable across renders, so it is safe in effect dependency lists. */
export function useApplyTabsOp() {
  const { id } = useCurrentAccount();
  return useSetAtom(tabsStateAtom(id));
}

export function useActiveTab(): Tab | undefined {
  const state = useTabsState();
  return useMemo(() => ops.getActiveTab(state), [state]);
}

export function useActiveTabId(): string | null {
  return useTabsState().activeTabId;
}

/**
 * Changes the route cannot express, so they are applied to state directly. Selecting anything that
 * has a URL of its own goes through `tabNavigation` instead.
 */
export function useTabActions() {
  const apply = useApplyTabsOp();
  return useMemo(
    () => ({
      /**
       * Route syncing never creates a scratch tab — `/editor` cannot tell one apart from another —
       * so this is the only way one comes into existence.
       */
      createScratchTab: () =>
        apply((state) =>
          ops.openTab(
            state,
            { kind: "scratch" },
            {
              newTabId: uuid(),
              cachedLabel: ops.nextScratchLabel(state.tabs),
            },
          ),
        ),
      /**
       * Only for picking among scratch tabs, which all share `/editor`. Every other tab is selected
       * by navigating to it, so that the URL stays the record of what is open.
       */
      activateTab: (tabId: string) =>
        apply((state) => ops.activateTab(state, tabId)),
      reorderTabs: (orderedIds: string[]) =>
        apply((state) => ops.reorderTabs(state, orderedIds)),
      setCachedLabel: (tabId: string, cachedLabel: string) =>
        apply((state) => ops.updateTab(state, tabId, { cachedLabel })),
    }),
    [apply],
  );
}

/**
 * Path of the tab the user was last on, for links into the editor from outside it. Search params are
 * intentionally omitted: consumers render this through `LinkWithSlug`, which owns the query string.
 */
export function useCurrentTabLink() {
  const activeTab = useActiveTab();
  return targetToPath(activeTab?.target ?? { kind: "scratch" });
}

/**
 * Latest preview per tab, so switching tabs restores the result last run there.
 *
 * In memory only, and keyed by tab id: preview results never outlive the session, so persisting the
 * id — as the previous implementation did — could only ever produce a dangling reference.
 */
const latestPreviewByTabAtom = atom<Record<string, string>>({});

export function useSetLatestPreviewForActiveTab() {
  const activeTabId = useActiveTabId();
  const setLatestPreviews = useSetAtom(latestPreviewByTabAtom);
  return useCallback(
    (previewId: string) => {
      if (!activeTabId) return;
      setLatestPreviews((prev) => ({ ...prev, [activeTabId]: previewId }));
    },
    [activeTabId, setLatestPreviews],
  );
}

export function useLatestPreviewByTab() {
  return useAtomValue(latestPreviewByTabAtom);
}
